import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const SenegalPage = () => {
  const senegalData = {
    name: 'Senegal',
    region: 'West Africa',
    color: '#606090',
    metaTitle: 'Pharma & Nutraceutical Registration in Senegal | CTD/WA-MRH | TheApostrophe',
    metaDescription: 'Learn how to register pharmaceuticals and nutraceuticals in Senegal. DPM requirements, WA-MRH CTD format, documentation, timelines, and regulatory insights for a stable West African market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Senegal',
      subtitle: 'A regulated, growing West African market aligned with WA-MRH CTD requirements, strong urban healthcare infrastructure, and consistent demand for chronic & essential medicines.',
      cta: 'Start Your Senegal Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~17.9M' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure per Capita', value: '~USD 60-70' },
      { label: 'Health Expenditure (% of GDP)', value: '~4-5%' },
      { label: 'Urbanisation', value: '~49% urban' },
      { label: 'Life Expectancy', value: '63 (men), 68 (women)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Stable, Import-Dependent, Urban-Driven',
      stats: [
        'Senegal imports most finished pharmaceuticals from India, France, Morocco, China, and Belgium',
        'Dakar acts as a regional medical hub for francophone West Africa',
        'Public procurement through PNA (Pharmacie Nationale d\'Approvisionnement)',
        'Private sector includes large pharmacy chains and private hospitals',
        'Nutraceutical and OTC categories expanding, especially in Dakar and Thiès',
        'One of West Africa\'s most stable political & regulatory environments'
      ],
      opportunities: [
        'High-potential for chronic care (HTN, diabetes, lipids)',
        'Anti-infectives with consistent demand',
        'Maternal & paediatric supplements',
        'Immunity, probiotics, herbal tonics',
        'Vitamins, minerals, antioxidants'
      ]
    },
    
    diseaseburden: {
      title: 'Understanding Healthcare Demand in Senegal',
      subtitle: 'Infectious diseases remain significant with rapidly rising NCDs and consistent maternal-child health needs.',
      categories: [
        {
          name: 'Infectious Diseases — Still Significant',
          items: [
            'Malaria remains endemic, especially outside Dakar',
            'TB prevalence ≈ 120-140 per 100,000 people',
            'HIV prevalence ~0.3-0.4%, one of the lowest in Africa',
            'Childhood diarrheal diseases common in low-resource regions',
            'Portfolio: Antimalarials, antibiotics, ORS + zinc, paediatric suspensions, antifungals'
          ]
        },
        {
          name: 'Rising Non-Communicable Diseases (NCDs)',
          items: [
            'Hypertension prevalence: 25-30% in urban adults',
            'Diabetes prevalence: ~2-3% but rising',
            'Obesity: Higher in urban women (10-15% range)',
            'Portfolio: Antihypertensives, antidiabetics, statins',
            'Nutra: Metabolic health supplements, omega-3, CoQ10, cardio-support nutraceuticals'
          ]
        },
        {
          name: 'Maternal & Child Health — High Demand',
          items: [
            'Ongoing needs for iron + folic acid',
            'Calcium + vitamin D for maternal health',
            'Pregnancy & lactation multivitamins',
            'Dewormers for paediatric health',
            'Paediatric multivitamins, probiotics',
            'Nutritional tonics (haematinics, appetite stimulants)'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'DPM – Direction de la Pharmacie et du Médicament',
      description: 'Ministry of Health & Social Action, Senegal. Senegal is part of WA-MRH (West African Medicines Regulatory Harmonization). DPM requires CTD-format submissions for pharmaceuticals aligned to WA-MRH modules. French is mandatory administrative language. Local agent/MAH required.',
      pharma: {
        timeline: 'Varies based on dossier completeness and DPM review process',
        requirements: [
          'CTD format aligned with WA-MRH (Modules 1-5)',
          'Module 1 - French administrative documents',
          'Stability data for Zone IVb (hot/humid tropical climate)',
          'Indian GMP accepted - DPM may request clarification or QMS documents',
          'Sample submission required for quality analysis in selected cases',
          'Common queries: COA/specifications, stability data, artwork in French, BE data',
          'MA valid for defined period with annual retention fees'
        ],
        insight: 'Senegal follows WA-MRH CTD structure. DPM review focuses on quality specifications, stability for tropical conditions, and French language compliance. Local MAH mandatory.'
      },
      nutra: {
        timeline: 'Depends on classification and documentation completeness',
        requirements: [
          'Full qualitative & quantitative composition',
          'COA & microbiological testing',
          'GMP / quality certification',
          'Safety justification for non-standard ingredients',
          'Artwork in French with compliant claims',
          'Claims must avoid therapeutic language',
          'Certificate of Free Sale (CFS) mandatory'
        ],
        insight: 'Senegal regulates supplements as compléments alimentaires (food supplements) following food-style documentation with safety and labelling controls. Urban Senegal shows strong appetite for immunity, energy tonics, women\'s health, and paediatric supplements.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration (CTD – WA-MRH)',
      steps: [
        'MAH / Local Representative - Senegal-registered company must submit application',
        'Dossier Format CTD - Module 1 (French) and Modules 2-5 (technical)',
        'Stability data for Zone IVb (hot/humid tropical climate)',
        'GMP Acceptance - Indian GMP accepted, DPM may request clarification',
        'Sample Submission - Required for quality analysis in selected cases',
        'Review & Queries - COA/specifications, stability, French artwork, BE data alignment',
        'Marketing Authorization approval - Valid for defined period',
        'Annual retention fees payment'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Dietary Supplement Registration (Senegal)',
      steps: [
        'Classification as compléments alimentaires (food supplements)',
        'Full qualitative & quantitative composition preparation',
        'COA & microbiological testing documentation',
        'GMP / quality certification',
        'Safety justification for non-standard ingredients',
        'French artwork with compliant claims (no therapeutic language)',
        'Stability documentation for liquids/tonics',
        'Certificate of Free Sale (CFS) submission',
        'DPM review and approval',
        'Marketing authorization issuance'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (CTD – WA-MRH)': [
        'Module 1 (French) administrative package',
        'CTD Modules 2-5',
        'GMP certificate',
        'CoPP (strongly preferred)',
        'Product specs + COAs',
        'API documentation',
        'Process validation',
        'Stability (Zone IVb)',
        'Labels + inserts in French',
        'Samples (if required)'
      ],
      'Nutraceuticals': [
        'Full formula (qualitative & quantitative)',
        'COAs',
        'GMP/quality certificate',
        'French artwork',
        'Stability (for liquids/tonics)',
        'CFS / Free Sale Certificate',
        'Claims justification'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Senegal Should Be Part of Your West Africa Expansion',
      reasons: [
        'Stable political & regulatory system',
        'High import reliance - ideal for Indian manufacturers',
        'Strong urban middle class - chronic & nutra products gain traction',
        'CTD alignment via WA-MRH - predictable dossier pathways',
        'French West Africa gateway - access to Mali, Niger, Burkina Faso, Côte d\'Ivoire',
        'Good market for OTC, nutraceuticals, and essential generics',
        'Dakar as regional medical hub for francophone West Africa',
        'Consistent demand for quality essential medicines'
      ],
      bestPortfolio: [
        'Antimalarials, antibiotics, chronic care / cardiometabolic',
        'Women\'s & paediatric supplements',
        'Herbal tonics, appetite stimulants, vitamin-mineral formulas'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Launch in Senegal',
      services: [
        'WA-MRH aligned CTD dossier development',
        'French artwork & label compliance',
        'Supplement classification support',
        'Ingredient safety verification',
        'Market feasibility & product selection',
        'Importer coordination (MAH support)',
        'Deficiency handling, renewals & lifecycle support'
      ]
    },
    
    faqs: [
      {
        question: 'Does Senegal follow CTD format?',
        answer: 'Yes. Senegal follows the WA-MRH CTD structure for pharmaceuticals.'
      },
      {
        question: 'Is French mandatory for labels?',
        answer: 'Yes. All pharmaceutical and supplement labels must be in French.'
      },
      {
        question: 'Are Indian GMP certificates accepted?',
        answer: 'Yes, Indian GMP certificates are widely accepted for registration purposes. DPM may request clarification or QMS documents.'
      },
      {
        question: 'Do supplements require registration?',
        answer: 'Yes. All imported supplements require prior approval from DPM.'
      },
      {
        question: 'Does Senegal allow herbal products?',
        answer: 'Yes, provided documentation (COA, safety, claims) meets DPM standards.'
      }
    ]
  };

  return <CountryTemplate countryData={senegalData} />;
};
